/**
 * Grid Hunter Engine — Crosshair Shooter
 *
 * Canvas 2D game engine for a modern Duck Hunt-style shooter.
 * Targets spawn on screen, player taps/clicks to shoot.
 * Scoring: accuracy, speed, combos, wave progression.
 *
 * Architecture: game state in plain objects, rAF loop,
 * React receives UI updates via bridge callback at ~10fps.
 */

// --- Audio (procedural Web Audio API) ---
let audioCtx = null;
function getAudioCtx() {
  if (!audioCtx) {
    try { audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
  }
  return audioCtx;
}

function playSound(type) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  switch (type) {
    case 'hit':
      osc.type = 'square';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.05);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
      break;
    case 'miss':
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
      break;
    case 'combo':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523, ctx.currentTime);
      osc.frequency.setValueAtTime(659, ctx.currentTime + 0.08);
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.16);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
      break;
    case 'wave':
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.3);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
      break;
    case 'gameover':
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 0.6);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.6);
      break;
    default: break;
  }
}

// --- Target Types ---
const TARGET_TYPES = [
  { id: 'standard', color: '#ff0040', points: 100, radius: 24, speed: 0, lifetime: 2.5, spawnWeight: 50 },
  { id: 'fast', color: '#ff6b00', points: 200, radius: 20, speed: 0, lifetime: 1.5, spawnWeight: 25 },
  { id: 'tiny', color: '#ffbb00', points: 300, radius: 14, speed: 0, lifetime: 2.0, spawnWeight: 15 },
  { id: 'moving', color: '#00d4ff', points: 250, radius: 22, speed: 120, lifetime: 3.0, spawnWeight: 10 },
  { id: 'decoy', color: '#555568', points: -200, radius: 26, speed: 0, lifetime: 3.0, spawnWeight: 8 },
];

function pickTargetType(wave) {
  const available = TARGET_TYPES.filter(t => {
    if (t.id === 'moving' && wave < 3) return false;
    if (t.id === 'decoy' && wave < 4) return false;
    if (t.id === 'tiny' && wave < 2) return false;
    return true;
  });
  const totalWeight = available.reduce((sum, t) => sum + t.spawnWeight, 0);
  let r = Math.random() * totalWeight;
  for (const t of available) {
    r -= t.spawnWeight;
    if (r <= 0) return t;
  }
  return available[0];
}

// --- Engine ---
class GridHunterEngine {
  constructor(canvas, uiBridge) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.uiBridge = uiBridge;
    this._rafId = null;
    this._lastTime = 0;
    this._uiTimer = 0;

    // Game state
    this.state = {
      score: 0,
      combo: 0,
      maxCombo: 0,
      wave: 1,
      hits: 0,
      misses: 0,
      shots: 0,
      targetsSpawned: 0,
      targetsPerWave: 8,
      waveTimer: 0,
      waveDuration: 20,
      spawnTimer: 0,
      spawnInterval: 1.8,
      gameOver: false,
      lives: 5,
      shakeX: 0,
      shakeY: 0,
      shakeDecay: 0,
    };

    this.targets = [];
    this.particles = [];
    this.hitMarkers = [];
    this.crosshair = { x: canvas.width / 2, y: canvas.height / 2 };

    // Input
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);

    canvas.addEventListener('mousemove', this._onPointerMove);
    canvas.addEventListener('mousedown', this._onPointerDown);
    canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    // Unlock audio
    const unlock = () => { getAudioCtx(); };
    canvas.addEventListener('touchstart', unlock, { once: true });
    canvas.addEventListener('click', unlock, { once: true });
  }

  start() {
    this._lastTime = performance.now();
    this._loop(this._lastTime);
  }

  stop() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this.canvas.removeEventListener('mousemove', this._onPointerMove);
    this.canvas.removeEventListener('mousedown', this._onPointerDown);
    this.canvas.removeEventListener('touchstart', this._onTouchStart);
  }

  resize(w, h) {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.ctx.scale(dpr, dpr);
    this.W = w;
    this.H = h;
  }

  // --- Main Loop ---
  _loop(now) {
    this._rafId = requestAnimationFrame((t) => this._loop(t));
    const dt = Math.min((now - this._lastTime) / 1000, 0.1);
    this._lastTime = now;

    if (this.state.gameOver) {
      this._render(dt);
      return;
    }

    this._update(dt);
    this._render(dt);

    // UI bridge throttled
    this._uiTimer += dt;
    if (this._uiTimer >= 0.1 && this.uiBridge) {
      this._uiTimer = 0;
      this.uiBridge({
        score: this.state.score,
        combo: this.state.combo,
        wave: this.state.wave,
        lives: this.state.lives,
        accuracy: this.state.shots > 0 ? Math.round((this.state.hits / this.state.shots) * 100) : 100,
        gameOver: this.state.gameOver,
        hits: this.state.hits,
        misses: this.state.misses,
        maxCombo: this.state.maxCombo,
        waveTime: Math.max(0, this.state.waveDuration - this.state.waveTimer),
      });
    }
  }

  _update(dt) {
    const st = this.state;

    // Wave timer
    st.waveTimer += dt;
    if (st.waveTimer >= st.waveDuration) {
      this._nextWave();
    }

    // Spawn targets
    st.spawnTimer += dt;
    if (st.spawnTimer >= st.spawnInterval && st.targetsSpawned < st.targetsPerWave) {
      st.spawnTimer = 0;
      this._spawnTarget();
    }

    // Update targets
    for (let i = this.targets.length - 1; i >= 0; i--) {
      const t = this.targets[i];
      t.age += dt;
      t.pulse = 0.8 + Math.sin(t.age * 6) * 0.2;

      // Movement
      if (t.speed > 0) {
        t.x += t.vx * dt;
        t.y += t.vy * dt;
        if (t.x < t.radius || t.x > this.W - t.radius) t.vx = -t.vx;
        if (t.y < 50 + t.radius || t.y > this.H - t.radius) t.vy = -t.vy;
      }

      // Lifetime expired
      if (t.age >= t.lifetime) {
        if (t.type !== 'decoy') {
          st.misses++;
          st.combo = 0;
          st.lives--;
          if (st.lives <= 0) {
            this._gameOver();
            return;
          }
        }
        this.targets.splice(i, 1);
      }
    }

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 200 * dt; // gravity
      p.life -= dt;
      if (p.life <= 0) this.particles.splice(i, 1);
    }

    // Update hit markers
    for (let i = this.hitMarkers.length - 1; i >= 0; i--) {
      const h = this.hitMarkers[i];
      h.age += dt;
      h.y -= 40 * dt;
      if (h.age > 0.8) this.hitMarkers.splice(i, 1);
    }

    // Screen shake decay
    if (st.shakeDecay > 0) {
      st.shakeDecay -= dt * 8;
      st.shakeX = (Math.random() - 0.5) * st.shakeDecay * 4;
      st.shakeY = (Math.random() - 0.5) * st.shakeDecay * 4;
    } else {
      st.shakeX = 0;
      st.shakeY = 0;
    }
  }

  _render(dt) {
    const ctx = this.ctx;
    const W = this.W;
    const H = this.H;

    ctx.save();
    ctx.translate(this.state.shakeX, this.state.shakeY);

    // Background
    ctx.fillStyle = '#06060c';
    ctx.fillRect(-5, -5, W + 10, H + 10);

    // Grid
    ctx.strokeStyle = 'rgba(255,0,64,0.04)';
    ctx.lineWidth = 1;
    const gridSize = 40;
    for (let x = 0; x < W; x += gridSize) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let y = 0; y < H; y += gridSize) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }

    // Targets
    for (const t of this.targets) {
      const alpha = t.age < 0.15 ? t.age / 0.15 : t.age > t.lifetime - 0.3 ? (t.lifetime - t.age) / 0.3 : 1;
      const r = t.radius * t.pulse;

      ctx.globalAlpha = alpha;

      // Glow
      ctx.shadowColor = t.color;
      ctx.shadowBlur = 15;

      // Outer ring
      ctx.strokeStyle = t.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(t.x, t.y, r, 0, Math.PI * 2);
      ctx.stroke();

      // Inner fill
      ctx.fillStyle = t.color + '20';
      ctx.fill();

      // Center dot
      ctx.fillStyle = t.color;
      ctx.beginPath();
      ctx.arc(t.x, t.y, 3, 0, Math.PI * 2);
      ctx.fill();

      // Lifetime bar
      if (t.type !== 'decoy') {
        const barW = r * 1.6;
        const barH = 3;
        const pct = 1 - t.age / t.lifetime;
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.fillRect(t.x - barW / 2, t.y + r + 6, barW, barH);
        ctx.fillStyle = pct > 0.3 ? t.color : '#ff0040';
        ctx.fillRect(t.x - barW / 2, t.y + r + 6, barW * pct, barH);
      }

      // Decoy X
      if (t.type === 'decoy') {
        ctx.strokeStyle = t.color;
        ctx.lineWidth = 2;
        const s = 8;
        ctx.beginPath(); ctx.moveTo(t.x - s, t.y - s); ctx.lineTo(t.x + s, t.y + s); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(t.x + s, t.y - s); ctx.lineTo(t.x - s, t.y + s); ctx.stroke();
      }

      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
    }

    // Particles
    for (const p of this.particles) {
      ctx.globalAlpha = p.life / p.maxLife;
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - 1.5, p.y - 1.5, 3, 3);
    }
    ctx.globalAlpha = 1;

    // Hit markers
    for (const h of this.hitMarkers) {
      const alpha = 1 - h.age / 0.8;
      ctx.globalAlpha = alpha;
      ctx.font = `bold 16px 'Orbitron', sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillStyle = h.color;
      ctx.shadowColor = h.color;
      ctx.shadowBlur = 8;
      ctx.fillText(h.text, h.x, h.y);
      ctx.shadowBlur = 0;
    }
    ctx.globalAlpha = 1;

    // Crosshair
    const cx = this.crosshair.x;
    const cy = this.crosshair.y;
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 1;
    const cSize = 16;
    const cGap = 4;
    // Top
    ctx.beginPath(); ctx.moveTo(cx, cy - cSize); ctx.lineTo(cx, cy - cGap); ctx.stroke();
    // Bottom
    ctx.beginPath(); ctx.moveTo(cx, cy + cGap); ctx.lineTo(cx, cy + cSize); ctx.stroke();
    // Left
    ctx.beginPath(); ctx.moveTo(cx - cSize, cy); ctx.lineTo(cx - cGap, cy); ctx.stroke();
    // Right
    ctx.beginPath(); ctx.moveTo(cx + cGap, cy); ctx.lineTo(cx + cSize, cy); ctx.stroke();
    // Center dot
    ctx.fillStyle = 'rgba(255,0,64,0.8)';
    ctx.beginPath(); ctx.arc(cx, cy, 2, 0, Math.PI * 2); ctx.fill();

    ctx.restore();
  }

  // --- Game Logic ---
  _spawnTarget() {
    const type = pickTargetType(this.state.wave);
    const margin = type.radius + 20;
    const t = {
      x: margin + Math.random() * (this.W - margin * 2),
      y: 60 + margin + Math.random() * (this.H - 60 - margin * 2),
      radius: type.radius,
      color: type.color,
      points: type.points,
      type: type.id,
      lifetime: type.lifetime * Math.max(0.5, 1 - this.state.wave * 0.03),
      speed: type.speed,
      vx: type.speed > 0 ? (Math.random() - 0.5) * type.speed * 2 : 0,
      vy: type.speed > 0 ? (Math.random() - 0.5) * type.speed * 2 : 0,
      age: 0,
      pulse: 1,
    };
    this.targets.push(t);
    this.state.targetsSpawned++;
  }

  _nextWave() {
    this.state.wave++;
    this.state.waveTimer = 0;
    this.state.targetsSpawned = 0;
    this.state.targetsPerWave = Math.min(30, 8 + this.state.wave * 2);
    this.state.spawnInterval = Math.max(0.4, 1.8 - this.state.wave * 0.12);
    this.state.waveDuration = Math.min(30, 20 + this.state.wave);
    playSound('wave');
  }

  _shoot(x, y) {
    if (this.state.gameOver) return;

    this.state.shots++;
    let hit = false;

    // Check targets (closest first for overlapping)
    let closestIdx = -1;
    let closestDist = Infinity;
    for (let i = 0; i < this.targets.length; i++) {
      const t = this.targets[i];
      const dx = x - t.x;
      const dy = y - t.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= t.radius && dist < closestDist) {
        closestDist = dist;
        closestIdx = i;
      }
    }

    if (closestIdx >= 0) {
      const t = this.targets[closestIdx];
      hit = true;

      if (t.type === 'decoy') {
        // Hit a decoy — penalty
        this.state.score = Math.max(0, this.state.score + t.points);
        this.state.combo = 0;
        this.state.lives--;
        this._emitParticles(t.x, t.y, '#555568', 8);
        this.hitMarkers.push({ x: t.x, y: t.y, text: t.points.toString(), color: '#ff0040', age: 0 });
        playSound('miss');
        this.state.shakeDecay = 1;
        if (this.state.lives <= 0) {
          this.targets.splice(closestIdx, 1);
          this._gameOver();
          return;
        }
      } else {
        // Good hit
        this.state.hits++;
        this.state.combo++;
        if (this.state.combo > this.state.maxCombo) this.state.maxCombo = this.state.combo;
        const comboMult = 1 + (this.state.combo - 1) * 0.1;
        const points = Math.round(t.points * comboMult);
        this.state.score += points;
        this._emitParticles(t.x, t.y, t.color, 12);
        this.hitMarkers.push({ x: t.x, y: t.y, text: '+' + points, color: t.color, age: 0 });
        this.state.shakeDecay = 0.5;

        if (this.state.combo > 1 && this.state.combo % 5 === 0) {
          playSound('combo');
        } else {
          playSound('hit');
        }
      }

      this.targets.splice(closestIdx, 1);
    } else {
      // Miss
      this.state.misses++;
      this.state.combo = 0;
      playSound('miss');
    }
  }

  _emitParticles(x, y, color, count) {
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const speed = 80 + Math.random() * 150;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        life: 0.4 + Math.random() * 0.3,
        maxLife: 0.7,
      });
    }
  }

  _gameOver() {
    this.state.gameOver = true;
    playSound('gameover');
    if (this.uiBridge) {
      this.uiBridge({
        score: this.state.score,
        combo: this.state.combo,
        wave: this.state.wave,
        lives: 0,
        accuracy: this.state.shots > 0 ? Math.round((this.state.hits / this.state.shots) * 100) : 0,
        gameOver: true,
        hits: this.state.hits,
        misses: this.state.misses,
        maxCombo: this.state.maxCombo,
        waveTime: 0,
      });
    }
  }

  // --- Input ---
  _onPointerMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    this.crosshair.x = (e.clientX - rect.left) * (this.W / rect.width);
    this.crosshair.y = (e.clientY - rect.top) * (this.H / rect.height);
  }

  _onPointerDown(e) {
    const rect = this.canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (this.W / rect.width);
    const y = (e.clientY - rect.top) * (this.H / rect.height);
    this.crosshair.x = x;
    this.crosshair.y = y;
    this._shoot(x, y);
  }

  _onTouchStart(e) {
    e.preventDefault();
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      const x = (touch.clientX - rect.left) * (this.W / rect.width);
      const y = (touch.clientY - rect.top) * (this.H / rect.height);
      this.crosshair.x = x;
      this.crosshair.y = y;
      this._shoot(x, y);
    }
  }
}

export default GridHunterEngine;
